import { useState } from 'react';

import cx from 'classnames';
import { AnimatePresence, motion } from 'motion/react';

import { type Masked, unmask } from '~/contentful/graphql';
import { flattenCollection, flattenSingletonCollection } from '~/contentful/utils/collection';
import { Event, trackEvent } from '~/ui/components/analytics';
import { useBreakpoint } from '~/ui/components/grid/useBreakpoint';
import { Link } from '~/ui/components/link';
import { Image } from '~/ui/modules/image';
import { type Image_AssetFragment } from '~/ui/modules/image/query';
import { Breakpoint } from '~/ui/styles/grid';
import typographyStyles from '~/ui/styles/typography.module.scss';
import { getInternalLinkFromFragment } from '~/ui/systems/internalLink';

import { type NavigationItem } from '../../navigation';
import styles from '../secondary.module.scss';
import { type SecondaryNavigationContent_QueryFragment } from './query';

interface NavigationItemData {
  label: string;
  href: string;
  image?: Masked<typeof Image_AssetFragment>;
}

type SecondaryNavigationContentProps = {
  data: Masked<typeof SecondaryNavigationContent_QueryFragment>;
  activeNavigation: NavigationItem | undefined;
  closeNavigation: () => void;
};

const MotionLink = motion.create(Link);

export function SecondaryNavigationContent({
  data,
  activeNavigation,
  closeNavigation,
}: SecondaryNavigationContentProps) {
  const breakpoint = useBreakpoint();
  const [hoveredSecondary, setHoveredSecondary] = useState<NavigationItemData>();

  const { mainNavigationCollection } =
    unmask<typeof SecondaryNavigationContent_QueryFragment>(data);

  const navigation = flattenSingletonCollection(mainNavigationCollection);

  let activeNavigationItems: NavigationItemData[] | undefined = undefined;
  switch (activeNavigation) {
    case 'ideas':
      const ideas = flattenCollection(navigation.ideasCollection);
      activeNavigationItems = ideas.map(idea => ({
        label: idea.name ?? '',
        href: getInternalLinkFromFragment(idea),
        image: idea.navigationImagePortrait ?? undefined,
      }));
      break;

    case 'pages':
      const pages = flattenCollection(navigation.pagesCollection);
      activeNavigationItems = pages.map(page => ({
        label: page.title ?? '',
        href: getInternalLinkFromFragment(page),
        image: page.navigationImagePortrait ?? undefined,
      }));
      break;
    case 'resources':
      const resources = flattenCollection(navigation.resourcesCollection);
      activeNavigationItems = resources.map(resource => ({
        label: resource.label ?? '',
        href: resource.reference ? getInternalLinkFromFragment(resource.reference) : '',
        image: resource.image ?? undefined,
      }));
      break;

    case 'search':
    default:
      break;
  }

  const hasVerticalNav = breakpoint === Breakpoint.DESKTOP || breakpoint === Breakpoint.LAPTOP;
  const additionalDelay = hasVerticalNav && !activeNavigation ? 0.4 : 0;

  return (
    <div className={styles.contentInner}>
      <div className={styles.itemsWrapper}>
        <AnimatePresence>
          {activeNavigationItems?.map(
            (item, index) =>
              item && (
                <MotionLink
                  className={styles.secondaryNavigationItem}
                  key={`${item.label}-${index}`}
                  initial={{ opacity: 0, transform: 'translateX(-10px)' }}
                  animate={{ opacity: 1, transform: 'translateX(0px)' }}
                  exit={{
                    opacity: 0,
                    transform: 'translateX(-10px)',
                    transition: {
                      duration: 0,
                    },
                  }}
                  transition={{ duration: 0.5, delay: additionalDelay + index * 0.05 }}
                  onMouseEnter={() => setHoveredSecondary(item)}
                  onMouseLeave={() => setHoveredSecondary(undefined)}
                  href={{
                    pathname: item.href,
                    query: { follow: false },
                  }}
                  as={item.href}
                  onNavigate={closeNavigation}
                  onClick={() =>
                    trackEvent(Event.NAVIGATION_SECONDARY, {
                      navigationCategory: activeNavigation,
                      navigationItem: item.label,
                      navigationLink: item.href,
                    })
                  }
                  // Firefox workaround: Motion runs opacity through the Web Animations API (WAAPI)
                  // when it can. In Firefox, the element shows its final opacity during the
                  // stagger `delay`, then snaps to 0 and fades in, so the fade looks like it
                  // plays twice. Passing an `onUpdate` handler makes Motion animate in JS
                  // instead of WAAPI, which avoids the bug. Remove once fixed upstream.
                  // https://motion.dev/docs/react-performance
                  onUpdate={() => {}}
                >
                  {item.image && (
                    <Image
                      image={item.image}
                      className={cx(styles.secondaryNavigationItemImage, styles.linkImage)}
                      fill={false}
                      width={48}
                      height={64}
                      aspectRatio="2/3"
                    />
                  )}
                  {item.label}
                </MotionLink>
              ),
          )}
        </AnimatePresence>
      </div>
      <div className={styles.contentRight}>
        <AnimatePresence>
          {hasVerticalNav && hoveredSecondary?.image ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { duration: 0.3, delay: 0.1 } }}
              exit={{ opacity: 0, transition: { duration: 0.4 } }}
              transition={{ ease: 'anticipate' }}
              className={styles.secondaryNavigationItemImage}
              key="image"
            >
              <Image
                image={hoveredSecondary.image}
                colSpan={{
                  [Breakpoint.MOBILE]: 4,
                  [Breakpoint.TABLET]: 7,
                }}
                aspectRatio="2/3"
              />
            </motion.div>
          ) : navigation.babyMissionStatement ? (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { duration: 0.3, delay: 0.1 } }}
              exit={{ opacity: 0, transition: { duration: 0.4 } }}
              className={cx(styles.statement, typographyStyles.blockquote)}
              transition={{ ease: 'anticipate' }}
            >
              {navigation.babyMissionStatement}
            </motion.p>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
}
